import type {
  Disclosure,
  DisclosureDefinition,
  DisclosurePageKey,
  DisclosurePageOutput,
  DisclosureSummaryOutput,
  NewDisclosure,
} from '@/types';
import { api } from './api';

const disclosureApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getDisclosures: builder.query<Disclosure[], number>({
      query: (inventoryId) => `inventories/${inventoryId}/disclosures`,
      providesTags: ['Disclosures'],
    }),
    getDisclosure: builder.query<Disclosure, number>({
      query: (id) => `disclosures/${id}`,
      providesTags: ['Disclosures'],
    }),
    // `/summary` and `/page/<key>` are POST "simulate" endpoints: the
    // body carries the current (possibly unsaved) `definition` so village
    // renders the report against it without persisting. Because the
    // definition is part of the query arg (the cache key), editing the
    // period re-runs these queries and the report recomputes live; Save
    // persists separately. An absent definition renders the saved
    // disclosure. The community definition is wire-identical FE<->village,
    // so it's sent as-is. Mirrors suit's disclosure queries.
    getDisclosureSummary: builder.query<
      DisclosureSummaryOutput,
      { id: number; definition?: DisclosureDefinition; scenario?: number }
    >({
      query: ({ id, definition, scenario }) => ({
        url: `disclosures/${id}/summary`,
        method: 'POST',
        body: {
          ...(definition ? { definition } : {}),
          ...(scenario != null ? { scenario } : {}),
        },
      }),
      providesTags: ['Disclosures'],
    }),
    getDisclosurePage: builder.query<
      DisclosurePageOutput,
      {
        id: number;
        key: DisclosurePageKey;
        definition?: DisclosureDefinition;
        scenario?: number;
      }
    >({
      query: ({ id, key, definition, scenario }) => ({
        url: `disclosures/${id}/page/${key}`,
        method: 'POST',
        body: {
          ...(definition ? { definition } : {}),
          ...(scenario != null ? { scenario } : {}),
        },
      }),
      providesTags: ['Disclosures'],
    }),
    newDisclosure: builder.mutation<Disclosure, NewDisclosure>({
      query: ({ inventoryId, name, definition }) => ({
        url: `inventories/${inventoryId}/disclosures`,
        method: 'POST',
        body: { name, definition },
      }),
      invalidatesTags: ['Disclosures'],
    }),
    updateDisclosure: builder.mutation<
      Disclosure,
      {
        id: number;
        name: string;
        definition: DisclosureDefinition;
        scenario?: number;
      }
    >({
      query: ({ id, name, definition, scenario }) => ({
        url: `disclosures/${id}`,
        method: 'POST',
        body: {
          name,
          definition,
          ...(scenario != null ? { scenario } : {}),
        },
      }),
      invalidatesTags: ['Disclosures'],
    }),
    // Export the disclosure's per-type deliverable(s) as a zip. Mirrors suit's
    // `exportDisclosure`: a POST with no body (the *saved* disclosure is
    // exported), typed `void` to callers — the signed download URL the server
    // returns is followed in `onQueryStarted`, so the caller just fires it.
    exportDisclosure: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `disclosures/${id}/export`,
        method: 'POST',
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        queryFulfilled.then(({ data }: any) => {
          window.location = data.url;
        });
      },
    }),
    // Download one evidence document from the Methodology page. Same shape as
    // `exportDisclosure`: typed `void`, the signed URL the server returns is
    // followed in `onQueryStarted`. The locked transfer-download pattern.
    downloadEvidence: builder.mutation<
      void,
      { inventoryId: number; evidenceId: number }
    >({
      query: ({ inventoryId, evidenceId }) => ({
        url: `inventories/${inventoryId}/evidence/${evidenceId}/download`,
        method: 'GET',
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        queryFulfilled.then(({ data }: any) => {
          window.location = data.url;
        });
      },
    }),
  }),
});

export const {
  useGetDisclosuresQuery,
  useGetDisclosureQuery,
  useGetDisclosureSummaryQuery,
  useGetDisclosurePageQuery,
  useNewDisclosureMutation,
  useUpdateDisclosureMutation,
  useExportDisclosureMutation,
  useDownloadEvidenceMutation,
} = disclosureApi;
